import React from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

// Replaces {{1}}, {{2}}, etc. with values from variables[prefix + num]
const injectVariables = (text, vars, prefix) => {
  if (!text) return "";
  return text.replace(/{{\s*(\d+)\s*}}/g, (_, num) => {
    const key = `${prefix}${num}`;   // e.g. header1
    const fallbackKey = prefix;      // e.g. header
    return vars[key] ?? vars[fallbackKey] ?? `[Configure ${key}]`;
  });
}

const TemplatePreview = ({ selectedTemplate, onClose, variables }) => {
  if (!selectedTemplate) return null;

  const headerComponent = selectedTemplate?.components?.find(
    (c) => c.type === "HEADER"
  );
  const bodyComponent = selectedTemplate?.components?.find(
    (c) => c.type === "BODY"
  );
  const footerComponent = selectedTemplate?.components?.find(
    (c) => c.type === "FOOTER"
  );
  const buttonsComponent = selectedTemplate?.components?.find(
    (c) => c.type === "BUTTONS"
  );

  const headerType = headerComponent?.format || "None";

  // Replace separately
  const headerText =
    headerType === "TEXT"
      ? injectVariables(headerComponent?.text || "", variables, "header")
      : "";
  const bodyContentRaw = injectVariables(
    bodyComponent?.text || "",
    variables,
    "body"
  );
  const footerText = injectVariables(
    footerComponent?.text || "",
    variables,
    "footer"
  );

  const bodyContent = bodyContentRaw
    .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");

  const mediaPreview = null;

  return (
    <motion.div
      className="bg-[#F4F0EC] p-6 rounded-2xl shadow-sm w-full h-fit relative"
      initial={{ x: 50, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 50, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-gray-500 hover:text-red-500 transition duration-200"
        aria-label="Close preview"
      >
        <X size={22} />
      </button>

      <h3 className="text-xl font-semibold text-blue-700 mb-4 text-center">
        Template Preview
      </h3>

      {/* Message Preview Bubble */}
      <div
        className="bg-cover bg-center rounded-lg p-3"
        style={{
          backgroundImage:
            "url('https://e0.pxfuel.com/wallpapers/722/149/desktop-wallpaper-message-background-whatsapp-message-background.jpg')",
        }}
      >
        <div className="bg-white p-4 rounded-xl shadow-md max-w-3xl mx-auto space-y-2">
          {/* Header */}
          {headerType !== "None" && (
            <div className="font-bold">
              {headerType === "TEXT" ? (
                headerText
              ) : mediaPreview ? (
                <img
                  src={mediaPreview}
                  alt="Media Preview"
                  className="w-full h-auto rounded"
                />
              ) : (
                <p className="italic text-gray-400">[{headerType} Preview]</p>
              )}
            </div>
          )}

          {/* Body */}
          <div
            className="text-gray-800"
            dangerouslySetInnerHTML={{ __html: bodyContent }}
          />

          {/* Footer */}
          {footerText && <p className="text-sm text-gray-500">{footerText}</p>}

          {/* Buttons */}
          {buttonsComponent?.buttons?.length > 0 && (
            <div className="pt-3 flex flex-col gap-2">
              {buttonsComponent.buttons.map((btn, idx) => {
                const baseClass =
                  "text-sm font-medium px-4 py-2 rounded-md border border-gray-300 shadow-sm transition duration-150 flex items-center justify-center";

                if (btn.type === "QUICK_REPLY") {
                  return (
                    <button
                      key={idx}
                      className={`${baseClass} bg-white text-gray-800 hover:bg-gray-100`}
                    >
                      {btn.text}
                    </button>
                  );
                }

                if (btn.type === "PHONE_NUMBER") {
                  return (
                    <a
                      key={idx}
                      href={`tel:${btn.phone_number}`}
                      className={`${baseClass} bg-white text-blue-600 hover:bg-blue-50`}
                    >
                      {btn.text}
                    </a>
                  );
                }

                if (btn.type === "URL") {
                  return (
                    <a
                      key={idx}
                      href={btn.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${baseClass} bg-white text-green-600 hover:bg-green-50`}
                    >
                      {btn.text}
                    </a>
                  );
                }

                return null;
              })}
            </div>
          )}

          <p className="text-xs text-gray-400 text-right mt-3">18:10</p>
        </div>
      </div>
    </motion.div>
  );
};

export default TemplatePreview;
